import React, { PureComponent } from 'react';
import { Card, CardContent, Typography, Box, Skeleton, IconButton, Button } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { withStyles } from '@mui/styles';
import { marked } from 'marked';
import { UrlBuilder } from "../../core/url/UrlBuilder";
import { UserLocalStorage } from "../../core/storage/UserLocalStorage";
import AiChatButton from './AiChatButton';

const DIGEST_CACHE_KEY = 'weekly_digest_content';
const DIGEST_WEEK_KEY = 'weekly_digest_week';
const DIGEST_DISMISSED_KEY = 'weekly_digest_dismissed';
const DIGEST_MINIMIZED_KEY = 'weekly_digest_minimized';
const DIGEST_CHAT_ID_KEY = 'weekly_digest_chat_id';
const SEVEN_DAYS_SECONDS = 7 * 24 * 60 * 60;

const styles = theme => ({
    card: {
        borderColor: theme.palette.secondary.main,
        borderWidth: 1,
    },
    content: {
        padding: `${theme.spacing(1.5)} !important`,
        '& p': { margin: '4px 0' },
        '& ul': { margin: '4px 0', paddingLeft: 20 },
        '& li': { marginBottom: 2 },
    },
});

function getIsoWeek(date) {
    const target = new Date(date.valueOf());
    const dayNum = (target.getDay() + 6) % 7; // Mon=0, Sun=6
    target.setDate(target.getDate() - dayNum + 3); // nearest Thursday
    const jan1 = new Date(target.getFullYear(), 0, 1);
    const weekNum = 1 + Math.round(((target - jan1) / 86400000 - 3 + ((jan1.getDay() + 6) % 7)) / 7);
    return `${target.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

function getPreviousIsoWeek() {
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    return getIsoWeek(lastWeek);
}

class WeeklyDigestCard extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            digestText: '',
            loading: false,
            visible: false,
            minimized: false,
            chatId: null,
            creditsRemaining: null,
        };
        this.streamBuffer = '';
        this.fullText = '';
        this.rafId = null;
    }

    componentDidMount() {
        const currentWeek = getPreviousIsoWeek();
        const dismissedWeek = UserLocalStorage.get(DIGEST_DISMISSED_KEY);
        if (dismissedWeek === currentWeek) return;

        const minimizedWeek = UserLocalStorage.get(DIGEST_MINIMIZED_KEY);
        const isMinimized = minimizedWeek === currentWeek;

        const cachedWeek = UserLocalStorage.get(DIGEST_WEEK_KEY);
        const cachedContent = UserLocalStorage.get(DIGEST_CACHE_KEY);

        if (cachedWeek === currentWeek && cachedContent) {
            const cachedChatId = UserLocalStorage.get(DIGEST_CHAT_ID_KEY);
            this.setState({ visible: true, minimized: isMinimized, digestText: cachedContent, chatId: cachedChatId ? parseInt(cachedChatId) : null });
            return;
        }

        this.fetchDigest();
    }

    componentWillUnmount() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    fetchDigest = async () => {
        this.setState({ loading: true, visible: true });

        try {
            const response = await fetch(UrlBuilder.ai.weeklyDigest());

            if (response.status === 402 || response.status === 401) {
                this.setState({ loading: false, visible: false });
                return;
            }

            const contentType = response.headers.get('content-type') || '';

            if (contentType.includes('application/json')) {
                const data = await response.json();
                if (data.cached && data.content) {
                    this.cacheDigest(data.content, data.chatId);
                    this.setState({ loading: false, digestText: data.content, chatId: data.chatId, creditsRemaining: data.creditsRemaining });
                    return;
                }
                this.setState({ loading: false, visible: false });
                return;
            }

            await this.consumeSseStream(response);
        } catch {
            this.setState({ loading: false, visible: false });
        }
    };

    cacheDigest = (content, chatId) => {
        const currentWeek = getPreviousIsoWeek();
        UserLocalStorage.set(DIGEST_WEEK_KEY, currentWeek, SEVEN_DAYS_SECONDS);
        UserLocalStorage.set(DIGEST_CACHE_KEY, content, SEVEN_DAYS_SECONDS);
        if (chatId) UserLocalStorage.set(DIGEST_CHAT_ID_KEY, String(chatId), SEVEN_DAYS_SECONDS);
    };

    dismiss = () => {
        const currentWeek = getPreviousIsoWeek();
        UserLocalStorage.set(DIGEST_DISMISSED_KEY, currentWeek, SEVEN_DAYS_SECONDS);
        this.setState({ visible: false });
    };

    minimize = () => {
        const currentWeek = getPreviousIsoWeek();
        UserLocalStorage.set(DIGEST_MINIMIZED_KEY, currentWeek, SEVEN_DAYS_SECONDS);
        this.setState({ minimized: true });
    };

    expand = () => {
        UserLocalStorage.remove(DIGEST_MINIMIZED_KEY);
        this.setState({ minimized: false });
    };

    bufferStreamChunk = (text) => {
        this.streamBuffer += text;
        if (!this.rafId) {
            this.rafId = requestAnimationFrame(this.flushStreamBuffer);
        }
    };

    flushStreamBuffer = () => {
        this.rafId = null;
        if (!this.streamBuffer) return;
        const toAppend = this.streamBuffer;
        this.streamBuffer = '';
        this.setState(prev => ({ digestText: prev.digestText + toAppend }));
    };

    consumeSseStream = async (response) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let hasStartedStreaming = false;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop();

            for (const part of parts) {
                const dataLine = part.split('\n').find(l => l.startsWith('data: '));
                if (!dataLine) continue;

                let parsed;
                try { parsed = JSON.parse(dataLine.substring(6)); } catch { continue; }

                if (parsed.type === 'chunk') {
                    this.fullText += parsed.text;
                    if (!hasStartedStreaming) {
                        hasStartedStreaming = true;
                        this.setState({ loading: false, digestText: parsed.text });
                    } else {
                        this.bufferStreamChunk(parsed.text);
                    }
                } else if (parsed.type === 'done') {
                    if (this.rafId) {
                        cancelAnimationFrame(this.rafId);
                        this.rafId = null;
                    }
                    this.flushStreamBuffer();
                    this.cacheDigest(this.fullText, parsed.chatId);
                    this.setState({ chatId: parsed.chatId, creditsRemaining: parsed.creditsRemaining });
                } else if (parsed.type === 'error') {
                    this.setState({ loading: false, visible: false });
                    return;
                }
            }
        }
    };

    render() {
        const { classes } = this.props;
        const { visible, loading, digestText, creditsRemaining, minimized } = this.state;

        if (!visible) return null;

        return (
            <Box pt={2.5}>
            <Card variant="outlined" className={classes.card}>
                <CardContent className={classes.content}>
                    <Box display="flex" alignItems="center" gap={0.75} mb={minimized ? 0 : 0.5}>
                        <AutoAwesomeIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                        <Typography variant="subtitle2" color="secondary" sx={{ flex: 1 }}>
                            Last Week's Digest
                        </Typography>
                        {minimized ? (
                            <IconButton size="small" onClick={this.expand} sx={{ p: 0.25 }}>
                                <ExpandMoreIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        ) : (
                            <>
                                <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>
                                    {creditsRemaining != null ? `${creditsRemaining} credit${creditsRemaining !== 1 ? 's' : ''} left` : ''}
                                </Typography>
                                <IconButton size="small" onClick={this.dismiss} sx={{ p: 0.25 }} title="Dismiss">
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton size="small" onClick={this.minimize} sx={{ p: 0.25 }} title="Minimize">
                                    <ExpandLessIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </>
                        )}
                    </Box>

                    {!minimized && (
                        <>
                            {loading ? (
                                <>
                                    <Skeleton animation="wave" variant="text" width="90%" />
                                    <Skeleton animation="wave" variant="text" width="75%" />
                                    <Skeleton animation="wave" variant="text" width="60%" />
                                </>
                            ) : (
                                <>
                                    <Typography
                                        variant="body2"
                                        component="div"
                                        sx={{ '& h1,& h2,& h3,& h4': { fontSize: '0.95rem', fontWeight: 600, mt: 1, mb: 0.25 } }}
                                        dangerouslySetInnerHTML={{ __html: marked.parse(digestText || '') }}
                                    />
                                    {this.state.chatId && (
                                        <Box display="flex" justifyContent="center">
                                        <AiChatButton
                                            triggerComponent={
                                                <Button
                                                    variant="outlined"
                                                    color="secondary"
                                                    size="small"
                                                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                                                    sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}
                                                >
                                                    Chat about your week
                                                </Button>
                                            }
                                            initialChatId={this.state.chatId}
                                            initialMessage={digestText}
                                            pageContext="home"
                                            presetSuggestions={[
                                                "What should I focus on this week?",
                                                "How does this compare to last week?",
                                                "Suggest a workout plan",
                                            ]}
                                        />
                                        </Box>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
            </Box>
        );
    }
}

export default withStyles(styles)(WeeklyDigestCard);
